// react
import React, { useMemo } from 'react';
import styled from 'styled-components';

// components
import { Avatar, Card, Title, Icon } from '@/components/atoms';

// models
import { Chat, ChatMessage } from '@karehero/models';

export interface ConversationCardProps {
  accountID: string;
  messages: ChatMessage[];
  createdAt: string;
}

export const ConversationCard = ({
  accountID,
  messages,
  createdAt,
}: ConversationCardProps) => {
  // memos
  const lastMessagePreview = useMemo(() => {
    if (!messages?.length) return 'No messages yet';
    const lastMessage = messages[messages.length - 1];
    return lastMessage.content;
  }, [messages]);

  const participants = useMemo(() => {
    // get all participant names which are not the current user
    const participantNames = messages
      .filter((message) => message.accountId !== accountID)
      // deduplicate names
      .reduce((acc, message) => {
        if (!acc.includes(message.name)) {
          acc.push(message.name);
        }
        return acc;
      }, [] as string[]);
    return participantNames.join(', ');
  }, [messages, accountID]);

  return (
    <StyledCard color='blue' isBordered={false} isPaddingSm>
      <Avatar fallback={participants?.[0] || ''} size={'xs'} />
      <LabelContainer>
        <StyledTitle>{participants}</StyledTitle>
        <ContentWrapper>
          <MessagePreview>{lastMessagePreview}</MessagePreview>
          <Icon icon='circle' type='fas' fontSize={3} />
          <Timestamp>{createdAt}</Timestamp>
        </ContentWrapper>
      </LabelContainer>
    </StyledCard>
  );
};

const StyledTitle = styled.div`
  font: ${(props) => props.theme.typography.body.sm.regular};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
  flex: 1;

  font: ${(props) => props.theme.typography.body.sm.regular};
  color: ${(props) => props.theme.color.neutral[70]};
`;

const MessagePreview = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Timestamp = styled.div`
  text-align: right;
`;

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;

const StyledCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[16]};
`;

ConversationCard.displayName = 'ConversationCard';

export default ConversationCard;
